import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Input, TextArea } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { Building2, Save } from "lucide-react";

/**
 * AdminBillingSeller — edit the "Bill from" details that appear on every
 * VenturaZ invoice. Reads/writes /api/admin/billing/seller.
 */
export default function AdminBillingSeller() {
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/billing/seller");
        setSeller(r.data || {});
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k) => (e) => setSeller({ ...seller, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/admin/billing/seller", seller);
      setSeller(r.data || seller);
      toast.success("Seller details saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading || !seller) {
    return (
      <DashboardShell>
        <div className="p-8 text-ink-muted">Loading…</div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-8">
        <div className="overline">BILLING</div>
        <h1 className="font-display text-3xl lg:text-4xl mt-1">Invoice seller identity</h1>
        <div className="text-sm text-ink-soft mt-1 mb-6">
          These details appear on every invoice PDF as the "Bill from" party. Must match your GST + MCA records.
        </div>

        <Card testId="billing-seller-card">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Legal name" value={seller.name || ""} onChange={set("name")}
              testId="seller-name" />
            <Input label="Brand line (appears at top)" value={seller.brand || ""} onChange={set("brand")}
              testId="seller-brand" />
            <Input label="GSTIN" value={seller.gstin || ""} onChange={set("gstin")}
              testId="seller-gstin" />
            <Input label="CIN" value={seller.cin || ""} onChange={set("cin")}
              testId="seller-cin" />
            <Input label="PAN" value={seller.pan || ""} onChange={set("pan")}
              testId="seller-pan" />
            <Input label="State (name)" value={seller.state || ""} onChange={set("state")}
              testId="seller-state" />
            <Input label="State code (numeric)" value={seller.state_code || ""} onChange={set("state_code")}
              testId="seller-state-code" />
            <Input label="Invoice prefix" value={seller.invoice_prefix || ""}
              onChange={set("invoice_prefix")} testId="seller-prefix" />
            <Input label="HSN / SAC" value={seller.hsn_sac || ""} onChange={set("hsn_sac")}
              testId="seller-hsn" />
            <Input label="Email" value={seller.email || ""} onChange={set("email")}
              testId="seller-email" />
            <Input label="Phone" value={seller.phone || ""} onChange={set("phone")}
              testId="seller-phone" />
            <Input label="Website" value={seller.website || ""} onChange={set("website")}
              testId="seller-website" />
          </div>
          <div className="mt-3">
            <TextArea label="Address" value={seller.address_line1 || ""} onChange={set("address_line1")}
              testId="seller-address" rows={2} />
          </div>
          <div className="mt-3">
            <TextArea label="Invoice terms" value={seller.terms || ""} onChange={set("terms")}
              testId="seller-terms" rows={2} />
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="text-xs text-ink-muted inline-flex items-center gap-1">
              <Building2 size={12} /> Changes apply immediately to all future invoices.
            </div>
            <Button variant="gold" onClick={save} disabled={busy} data-testid="seller-save">
              <Save size={14} className="inline mr-1" /> {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </Card>
      </div>
    </DashboardShell>
  );
}
